import React, { useEffect, useRef } from 'react';
import SEO from '../components/SEO';
import TrailButton from '../components/TrailButton';
import { openBookingModal } from '../components/BookingModal';
import { ChevronRight, Shield, Clock, Wrench, Users, Zap, PhoneCall, MessageSquare, Calendar, BarChart3, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import '../styles/simpletalk.css';

const SimpleTalkAlternative = () => {
  const testimonialRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const container = testimonialRef.current;
    if (!container) return;
    while (container.firstChild) container.removeChild(container.firstChild);
    const iframe = document.createElement('iframe');
    iframe.id = 'testimonialto-skyai-st-light';
    iframe.src = 'https://embed-v2.testimonial.to/w/skyai?animated=on&theme=light&shadowColor=ffffff&tag=all&fontFamily=Poppins';
    iframe.frameBorder = '0';
    iframe.scrolling = 'no';
    iframe.style.cssText = 'width:100%;border:none;overflow:hidden;';
    container.appendChild(iframe);
    const script = document.createElement('script');
    script.src = 'https://embed-v2.testimonial.to/iframeResizer.min.js';
    script.onload = () => {
      if (window.iFrameResize) window.iFrameResize({ log: false, checkOrigin: false }, `#${iframe.id}`);
    };
    document.body.appendChild(script);
    return () => {
      while (container.firstChild) container.removeChild(container.firstChild);
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  const scrollToBooking = () => openBookingModal();

  const CTA = ({ label }) => (
    <TrailButton>
      <button className="st-cta" onClick={scrollToBooking} data-testid="st-cta-btn">
        {label || 'Book My AI Growth Planning Call'} <ChevronRight size={18} />
      </button>
    </TrailButton>
  );

  return (
    <div className="st-page" data-testid="simpletalk-page">
      <SEO
        title="SimpleTalk AI Alternative | Custom AI Voice Agents — Skye AI"
        description="SimpleTalk AI shut down. Get a custom-built AI voice system that's fully managed, never disappears, and goes live in 30 days. Book a free Growth Planning Call."
        keywords="SimpleTalk alternative, SimpleTalk AI replacement, AI voice agent, SimpleTalk shut down, SimpleTalk bankruptcy, AI calling platform alternative"
        path="/simpletalk-alternative"
      />

      {/* ── Topbar ── */}
      <header className="st-topbar" data-testid="st-topbar">
        <a href="/">
          <img src="https://perspective.imgix.net/68780c456625fd003b27e7c2.png?auto=format,compress&dpr=2&fit=crop&h=32&q=80" alt="Skye AI" className="st-topbar__logo" />
        </a>
      </header>

      {/* ── Urgency Bar ── */}
      <div className="st-urgency" data-testid="st-urgency-bar">
        <div className="st-urgency__dot" />
        <span>SimpleTalk AI has filed for bankruptcy. If you're affected, we can help you migrate — fast.</span>
      </div>

      {/* ── Hero ── */}
      <section className="st-hero" data-testid="st-hero">
        <div className="st-container">
          <div className="st-hero__badge">For businesses affected by the SimpleTalk shutdown</div>
          <h1 className="st-hero__title">
            SimpleTalk Shut Down.<br />
            <span className="st-gradient">Your AI Voice System Doesn't Have To.</span>
          </h1>
          <p className="st-hero__sub">
            We build custom AI voice agents, follow-up systems, and booking automation for your business —
            fully managed by our team. No DIY platforms. No more vendor risk. Live in 30 days.
          </p>
          <div className="st-hero__cta">
            <CTA label="Get Your Free Migration Plan" />
            <span className="st-hero__note">30-min call. Zero commitment. We'll map out your replacement system.</span>
          </div>

          <div className="st-hero__stats">
            <div className="st-hero__stat">
              <b>300+</b>
              <span>Businesses served</span>
            </div>
            <div className="st-hero__stat">
              <b>30 days</b>
              <span>To go live</span>
            </div>
            <div className="st-hero__stat">
              <b>24/7</b>
              <span>AI systems running</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem Section ── */}
      <section className="st-section st-section--alt" data-testid="st-problem">
        <div className="st-container">
          <div className="st-eyebrow">The Problem</div>
          <h2 className="st-h2">Why SaaS Dependency Is a Business Risk</h2>
          <p className="st-p">
            SimpleTalk's shutdown left hundreds of businesses scrambling. Their AI voice agents went dark,
            calls stopped getting answered, and leads started falling through the cracks — overnight.
          </p>
          <div className="st-problem-grid">
            <div className="st-problem-card">
              <XCircle className="st-problem-card__icon st-problem-card__icon--red" size={24} />
              <h3>Platform Disappears</h3>
              <p>When a SaaS tool shuts down, your entire system goes with it. No warning, no fallback.</p>
            </div>
            <div className="st-problem-card">
              <XCircle className="st-problem-card__icon st-problem-card__icon--red" size={24} />
              <h3>You're On Your Own</h3>
              <p>DIY platforms leave you to build, configure, and maintain everything yourself. When it breaks, it's your problem.</p>
            </div>
            <div className="st-problem-card">
              <XCircle className="st-problem-card__icon st-problem-card__icon--red" size={24} />
              <h3>No Real Support</h3>
              <p>Generic help docs and ticket queues don't cut it when your AI stops booking appointments at 2am.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Solution Section ── */}
      <section className="st-section" data-testid="st-solution">
        <div className="st-container">
          <div className="st-eyebrow">The Skye AI Difference</div>
          <h2 className="st-h2">We Don't Give You a Tool. We Build Your System.</h2>
          <p className="st-p">
            Instead of handing you another login and wishing you luck, we build custom AI systems
            tailored to your business — and our team manages everything, ongoing.
          </p>
          <div className="st-solution-grid">
            {[
              { icon: PhoneCall, title: 'Custom AI Voice Agents', desc: 'Built for your business, your tone, your workflows. Not a template.' },
              { icon: MessageSquare, title: 'Automated Follow-Up', desc: 'Text, email, and voice follow-up that engages every lead instantly.' },
              { icon: Calendar, title: 'Appointment Booking', desc: 'AI books directly on your calendar — qualified leads only.' },
              { icon: Wrench, title: 'CRM Integration', desc: 'Connects with your existing tools. No double entry, no broken data.' },
              { icon: Users, title: 'Dedicated Team', desc: 'A real team builds, manages, and optimizes your AI systems.' },
              { icon: Shield, title: 'No Vendor Lock-In', desc: 'Your systems are custom-built. You\'re never at the mercy of one platform.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div className="st-solution-card" key={title}>
                <div className="st-solution-card__icon"><Icon size={22} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
          <div className="st-section__cta"><CTA /></div>
        </div>
      </section>

      {/* ── Comparison Table ── */}
      <section className="st-section st-section--alt" data-testid="st-comparison">
        <div className="st-container">
          <div className="st-eyebrow">Side-by-Side</div>
          <h2 className="st-h2">How Skye AI Compares</h2>
          <p className="st-p">Not all alternatives are created equal. Here's what actually matters when replacing your AI voice system.</p>

          <div className="st-table-wrap">
            <table className="st-table" data-testid="st-comparison-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th className="st-table__highlight">Skye AI</th>
                  <th>DIY Platforms</th>
                  <th>Traditional Agencies</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['AI Voice Agents', true, 'You build it', false],
                  ['Automated Follow-Up', true, 'You configure it', 'partial'],
                  ['Appointment Booking', true, 'You set it up', 'partial'],
                  ['CRM Integration', true, 'You connect it', true],
                  ['Custom-Built for You', true, false, false],
                  ['Dedicated Support Team', true, false, 'partial'],
                  ['Goes Live in 30 Days', true, 'Depends on you', false],
                  ['24/7 Monitoring', true, false, false],
                  ['Ongoing Optimization', true, false, 'partial'],
                  ['No Vendor Lock-In Risk', true, false, true],
                ].map(([feature, skye, diy, agency], i) => (
                  <tr key={i}>
                    <td className="st-table__feature">{feature}</td>
                    <td className="st-table__highlight">
                      {skye === true ? <CheckCircle2 size={18} className="st-check" /> : skye}
                    </td>
                    <td>
                      {diy === true ? <CheckCircle2 size={18} className="st-check" /> :
                       diy === false ? <XCircle size={18} className="st-x" /> :
                       diy === 'partial' ? <MinusCircle size={18} className="st-partial" /> :
                       <span className="st-muted">{diy}</span>}
                    </td>
                    <td>
                      {agency === true ? <CheckCircle2 size={18} className="st-check" /> :
                       agency === false ? <XCircle size={18} className="st-x" /> :
                       <MinusCircle size={18} className="st-partial" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Migration Timeline ── */}
      <section className="st-section" data-testid="st-timeline">
        <div className="st-container">
          <div className="st-eyebrow">Your Migration Path</div>
          <h2 className="st-h2">From Shutdown to Live in 30 Days</h2>
          <div className="st-timeline">
            {[
              { day: 'Day 1', title: 'Free Strategy Call', desc: 'We map your current setup, workflows, and what needs replacing.' },
              { day: 'Days 2–14', title: 'We Build Your System', desc: 'Custom voice agents, follow-up sequences, CRM connections — all tailored to you.' },
              { day: 'Days 15–25', title: 'Testing & QA', desc: 'We test every call flow, booking path, and integration before going live.' },
              { day: 'Day 30', title: 'You\'re Live', desc: 'Your new AI system is answering calls, booking appointments, and following up — 24/7.' },
            ].map(({ day, title, desc }) => (
              <div className="st-timeline__step" key={day}>
                <div className="st-timeline__left">
                  <span className="st-timeline__day">{day}</span>
                  <div className="st-timeline__dot" />
                </div>
                <div className="st-timeline__content">
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="st-section__cta"><CTA /></div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="st-section st-section--alt" data-testid="st-testimonials">
        <div className="st-container">
          <h2 className="st-h2">300+ Businesses Trust Skye AI</h2>
          <div ref={testimonialRef} className="st-testimonial-embed" data-testid="st-testimonial-embed" />
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="st-section" data-testid="st-faq">
        <div className="st-container st-container--narrow">
          <h2 className="st-h2">Common Questions</h2>
          <div className="st-faq-list">
            {[
              { q: 'Can you replace everything SimpleTalk did?', a: 'Yes. We build custom AI voice agents, automated follow-up, appointment booking, and CRM integrations. If SimpleTalk handled it, we can build a better version tailored to your business.' },
              { q: 'How fast can I get back up and running?', a: 'Most clients go live within 30 days. We start with a strategy call to map your current setup, then our team builds, tests, and launches your replacement system.' },
              { q: 'Do I have to set anything up myself?', a: 'No. This is 100% done for you. Our team handles everything — AI buildout, CRM connections, workflow setup, testing, and ongoing management.' },
              { q: 'What if I was using SimpleTalk with GoHighLevel?', a: 'We integrate with GoHighLevel, HubSpot, Salesforce, Jobber, and 50+ other tools. Your contacts, calendars, and pipelines stay connected.' },
              { q: 'Is there a long-term contract?', a: 'No long-term contracts. We earn your business month after month with real results. Our clients stay because the AI systems we build drive measurable ROI.' },
              { q: 'How is this different from switching to another platform?', a: 'Other platforms hand you a login and leave you to figure it out. We build custom AI systems for your business and manage them ongoing. You never have to worry about configuration, maintenance, or another platform shutting down.' },
            ].map(({ q, a }) => (
              <details className="st-faq-item" key={q}>
                <summary className="st-faq-q" data-testid="st-faq-question">{q}</summary>
                <p className="st-faq-a">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="st-final-cta" data-testid="st-final-cta">
        <div className="st-container">
          <h2>Don't Let a Shutdown Stop Your Growth.</h2>
          <p>Book a free Growth Planning Call and we'll map out your custom AI replacement — live in 30 days.</p>
          <CTA label="Book My Free Migration Call" />
          <div className="st-final-cta__trust">
            <span><Clock size={14} /> 30-min call</span>
            <span><Shield size={14} /> Zero commitment</span>
            <span><Zap size={14} /> Live in 30 days</span>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="st-footer" data-testid="st-footer">
        <a href="/privacy-policy">Skye AI LLC Privacy Policy</a>
        <span>|</span>
        <a href="/">Back to Skye AI</a>
      </footer>
    </div>
  );
};

export default SimpleTalkAlternative;
